import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Text } from "@andrum/ui";
import {
  fetchMyReferral,
  type ReferralMe,
} from "@/features/referrals/api/referrals-api";
import { hapticService } from "@/lib/capacitor";
import { clearEntitlementCache } from "@/lib/billing/entitlements";

export function InviteCard() {
  const { t } = useTranslation();
  const [data, setData] = useState<ReferralMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const me = await fetchMyReferral();
      if (!cancelled) {
        setData(me);
        setLoading(false);
        if (!me) setError(t("invite.loadError"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const share = async () => {
    if (!data) return;
    void hapticService.selection();
    setNote(null);
    const text = t("invite.shareText", {
      url: data.inviteUrl,
      days: data.rewardDaysPerInvite,
    });

    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({
          title: t("invite.shareTitle"),
          text,
          url: data.inviteUrl,
        });
        setNote(t("invite.shared"));
        return;
      }
    } catch {
      // user cancelled share — fall through to copy
    }

    try {
      await navigator.clipboard.writeText(data.inviteUrl);
      setNote(t("invite.copied"));
      void hapticService.success();
    } catch {
      setNote(data.inviteUrl);
    }
  };

  const copyCode = async () => {
    if (!data) return;
    void hapticService.selection();
    try {
      await navigator.clipboard.writeText(data.code);
      setNote(t("invite.codeCopied"));
    } catch {
      setNote(data.code);
    }
  };

  return (
    <Card className="space-y-3 p-4">
      <div className="space-y-1">
        <Text variant="label">{t("invite.title")}</Text>
        <Text variant="body" className="text-sm leading-relaxed text-foreground-muted">
          {t("invite.body", { days: data?.rewardDaysPerInvite ?? 7 })}
        </Text>
      </div>

      {loading ? (
        <Text variant="caption">{t("loadingShort")}</Text>
      ) : data ? (
        <>
          <div className="rounded-2xl border border-border bg-surface-muted/60 px-3.5 py-3">
            <Text variant="caption">{t("invite.yourCode")}</Text>
            <p className="mt-1 font-display text-2xl font-semibold tracking-[0.12em] text-foreground">
              {data.code}
            </p>
            <p className="mt-2 break-all text-xs leading-snug text-foreground-subtle">
              {data.inviteUrl}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-primary-soft/40 px-3 py-2.5 text-center">
              <p className="text-lg font-semibold tabular-nums text-primary">
                {data.invitedCount}
              </p>
              <p className="text-[11px] text-foreground-muted">
                {t("invite.statInvited")}
              </p>
            </div>
            <div className="rounded-xl bg-secondary-soft/50 px-3 py-2.5 text-center">
              <p className="text-lg font-semibold tabular-nums text-foreground">
                {data.rewardDaysEarned}
              </p>
              <p className="text-[11px] text-foreground-muted">
                {t("invite.statDays")}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button fullWidth onClick={() => void share()}>
              {t("invite.shareCta")}
            </Button>
            <Button fullWidth variant="soft" onClick={() => void copyCode()}>
              {t("invite.copyCode")}
            </Button>
          </div>

          <Text variant="caption" className="leading-relaxed">
            {t("invite.rules", { days: data.rewardDaysPerInvite })}
          </Text>
        </>
      ) : (
        <Text variant="caption" className="text-danger">
          {error}
        </Text>
      )}

      {note ? (
        <p className="text-sm text-secondary" role="status">
          {note}
        </p>
      ) : null}

      {/* Refresh entitlement cache when user returns after sharing */}
      <button
        type="button"
        className="hidden"
        onClick={() => clearEntitlementCache()}
        aria-hidden
      />
    </Card>
  );
}
